/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  ButtonBase,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
  DIGITACAO_STATUS,
  STATUS_COLOR,
  STATUS_LABELS,
  getMetric,
  sumMetrics,
} from './constants';

const TOTAL_KEY = 'TOTAL';

const CARDS = [
  { key: TOTAL_KEY, label: 'Total', icon: <FormatListBulletedIcon /> },
  {
    key: DIGITACAO_STATUS.AGUARDANDO_DIGITACAO,
    label: 'Aguardando',
    icon: <HourglassEmptyIcon />,
  },
  {
    key: DIGITACAO_STATUS.EM_DIGITACAO,
    label: 'Em Digitação',
    icon: <KeyboardIcon />,
  },
  {
    key: DIGITACAO_STATUS.DIGITACAO_FINALIZADA,
    label: 'Finalizada',
    icon: <CheckCircleOutlineIcon />,
  },
  {
    key: DIGITACAO_STATUS.EXECUTADA_GDIS,
    label: 'Executada GDIS',
    icon: <TaskAltIcon />,
  },
];

const resolveColor = (theme, colorKey) => {
  if (colorKey === 'default' || !theme.palette[colorKey]) {
    return theme.palette.text.secondary;
  }
  return theme.palette[colorKey].main;
};

const formatNumber = (n) => (n ?? 0).toLocaleString('pt-BR');

const KpiCell = ({ config, metric, active, onClick, compact, vertical, clickable }) => {
  const colorKey = STATUS_COLOR[config.key] || 'default';
  const label = config.key === TOTAL_KEY ? 'Total' : STATUS_LABELS[config.key];

  const inner = compact ? (
    <Stack
      direction={vertical ? 'row' : 'column'}
      spacing={vertical ? 1 : 0.15}
      alignItems={vertical ? 'center' : 'center'}
      justifyContent={vertical ? 'space-between' : 'center'}
      sx={{ minWidth: 0, width: '100%' }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        title={label}
        sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: '0.65rem', minWidth: 0 }}
      >
        {label}
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          lineHeight: 1.2,
          flexShrink: 0,
          color: (theme) => (active && clickable ? resolveColor(theme, colorKey) : 'text.primary'),
        }}
      >
        {formatNumber(metric.nsirs)}
      </Typography>
    </Stack>
  ) : (
    <Stack direction="row" spacing={1.75} alignItems="center" sx={{ width: '100%' }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: (theme) => resolveColor(theme, colorKey),
          backgroundColor: (theme) => `${resolveColor(theme, colorKey)}1F`,
          border: (theme) =>
            active && clickable ? `2px solid ${resolveColor(theme, colorKey)}` : '2px solid transparent',
        }}
      >
        {React.cloneElement(config.icon, { sx: { fontSize: 22 } })}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, lineHeight: 1.2 }}
          noWrap
          title={label}
        >
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35 }}>
          {formatNumber(metric.nsirs)} NSIRs
        </Typography>
        {metric.hasBreakdown ? (
          <>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, lineHeight: 1.35 }}
              color="text.primary"
            >
              {formatNumber(metric.servicosDigitados)}/{formatNumber(metric.servicosTotal)}{' '}
              serviços
            </Typography>
            {metric.servicosPendentes > 0 && (
              <Typography
                variant="caption"
                sx={{ lineHeight: 1.35, fontWeight: 600 }}
                color="warning.main"
              >
                {formatNumber(metric.servicosPendentes)} pendentes
              </Typography>
            )}
          </>
        ) : (
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, lineHeight: 1.35 }}
            color="text.primary"
          >
            {formatNumber(metric.servicosTotal)} serviços
          </Typography>
        )}
      </Box>
    </Stack>
  );

  const layoutSx = compact
    ? {
        flex: vertical ? '1 1 auto' : '0 0 auto',
        minWidth: vertical ? 0 : 88,
        width: vertical ? '100%' : 'auto',
        justifyContent: vertical ? 'space-between' : 'center',
        textAlign: vertical ? 'left' : 'center',
        px: vertical ? 1.5 : 1.25,
        py: vertical ? 1 : 0.75,
        borderRadius: 1,
      }
    : {
        flex: 1,
        minWidth: 190,
        justifyContent: 'flex-start',
        textAlign: 'left',
        px: 2.5,
        py: 2,
        borderRadius: 1,
      };

  if (!clickable) {
    return (
      <Box sx={{ ...layoutSx, display: 'flex', alignItems: 'center' }}>
        {inner}
      </Box>
    );
  }

  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        ...layoutSx,
        transition: 'background-color 0.2s ease',
        backgroundColor: (theme) =>
          active ? `${resolveColor(theme, colorKey)}14` : 'transparent',
        '&:hover': {
          backgroundColor: (theme) => `${resolveColor(theme, colorKey)}0F`,
        },
      }}
    >
      {inner}
    </ButtonBase>
  );
};

KpiCell.propTypes = {
  config: PropTypes.object.isRequired,
  metric: PropTypes.shape({
    nsirs: PropTypes.number,
    servicos: PropTypes.number,
    servicosTotal: PropTypes.number,
    servicosDigitados: PropTypes.number,
    servicosPendentes: PropTypes.number,
    hasBreakdown: PropTypes.bool,
  }).isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func,
  compact: PropTypes.bool,
  vertical: PropTypes.bool,
  clickable: PropTypes.bool,
};

KpiCell.defaultProps = {
  active: false,
  onClick: undefined,
  compact: false,
  vertical: false,
  clickable: true,
};

const KpiSkeleton = ({ compact }) => (
  <Box
    sx={{
      flex: compact ? '0 0 auto' : 1,
      minWidth: compact ? 88 : 190,
      px: compact ? 1.25 : 2.5,
      py: compact ? 0.75 : 2,
    }}
  >
    {compact ? (
      <Stack spacing={0.5} alignItems="center">
        <Skeleton width={56} height={12} />
        <Skeleton width={28} height={18} />
      </Stack>
    ) : (
      <Stack direction="row" spacing={1.75} alignItems="center">
        <Skeleton variant="circular" width={44} height={44} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width="60%" height={20} />
          <Skeleton width="40%" height={16} />
          <Skeleton width="55%" height={16} />
          <Skeleton width="45%" height={14} />
        </Box>
      </Stack>
    )}
  </Box>
);

KpiSkeleton.propTypes = {
  compact: PropTypes.bool,
};

KpiSkeleton.defaultProps = {
  compact: false,
};

const KpiStrip = ({
  resumo,
  loading,
  activeStatus,
  onSelectStatus,
  compact,
  vertical,
  clickable,
  totalPosition,
}) => {
  const { filtrado } = resumo;
  const cards = totalPosition === 'last'
    ? [...CARDS.filter((c) => c.key !== TOTAL_KEY), CARDS.find((c) => c.key === TOTAL_KEY)]
    : CARDS;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: vertical ? 'column' : 'row',
        flexWrap: vertical ? 'nowrap' : 'wrap',
        alignItems: 'stretch',
        ...(compact && {
          flex: vertical ? '1 1 200px' : '1 1 auto',
          minWidth: vertical ? 180 : 0,
          maxWidth: vertical ? 260 : 'none',
        }),
        '& > *:not(:first-of-type)': vertical
          ? {
              borderTop: 1,
              borderColor: 'divider',
            }
          : {
              borderLeft: { md: 1 },
              borderTop: { xs: 1, md: 0 },
              borderColor: { xs: 'divider', md: 'divider' },
            },
      }}
    >
      {loading
        ? cards.map((c) => <KpiSkeleton key={c.key} compact={compact} />)
        : cards.map((config) => {
            const metric =
              config.key === TOTAL_KEY
                ? sumMetrics(filtrado)
                : getMetric(filtrado, config.key);
            const isTotal = config.key === TOTAL_KEY;
            const active = clickable
              ? (isTotal ? activeStatus == null : activeStatus === config.key)
              : false;
            return (
              <KpiCell
                key={config.key}
                config={config}
                metric={metric}
                active={active}
                compact={compact}
                vertical={vertical}
                clickable={clickable}
                onClick={clickable ? () => {
                  if (isTotal) {
                    onSelectStatus(null);
                  } else {
                    onSelectStatus(activeStatus === config.key ? null : config.key);
                  }
                } : undefined}
              />
            );
          })}
    </Paper>
  );
};

KpiStrip.propTypes = {
  resumo: PropTypes.shape({
    filtrado: PropTypes.object,
    total: PropTypes.object,
  }).isRequired,
  loading: PropTypes.bool,
  activeStatus: PropTypes.string,
  onSelectStatus: PropTypes.func,
  compact: PropTypes.bool,
  vertical: PropTypes.bool,
  clickable: PropTypes.bool,
  totalPosition: PropTypes.oneOf(['first', 'last']),
};

KpiStrip.defaultProps = {
  loading: false,
  activeStatus: null,
  onSelectStatus: undefined,
  compact: false,
  vertical: false,
  clickable: true,
  totalPosition: 'first',
};

export default KpiStrip;
